// src/components/VerseSidebar.tsx
import { Box, Button, Divider, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { AiTwotoneAlert } from "react-icons/ai";
import { CurrentDebitInterface } from "../../common/interfaces";
import { verses } from "../../pages/login/versiculos";
import api from "../../services/api";
import { PaymentModal } from "../PaymentModal";
import { CardVerse } from "./Verse.styled";

// ---- Tipos da PTAX (BCB) ----
interface PtaxResponse {
  value: Array<{
    cotacaoCompra: number;
    cotacaoVenda: number;
    dataHoraCotacao: string;
  }>;
}

// ---- Tipos do débito ---- (crua e normalizada)
type CurrentDebitApi = {
  charged_amount: string;
  company_id: number;
  config_id: number;
  created_at: string;
  due_date: string;
  is_in_debt: number;
  paid_amount: string;
  payment_date: string | null;
  subscription_id: number;
  updated_at: string;
};

/**
 * useInterval robusto (corrige drift, evita overlaps, reancora ao voltar a aba)
 */
function useInterval(
  callback: () => void | Promise<void>,
  delay: number | null
) {
  const savedCallback = useRef(callback);
  const runningRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expectedRef = useRef<number | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay == null) return;

    const tick = async () => {
      if (!runningRef.current) {
        runningRef.current = true;
        try {
          await savedCallback.current();
        } finally {
          runningRef.current = false;
        }
      }

      const now = Date.now();
      if (expectedRef.current == null) {
        expectedRef.current = now + delay;
      } else {
        expectedRef.current += delay;
      }
      const drift = now - expectedRef.current;
      const nextDelay = Math.max(0, delay - (drift > 0 ? drift : 0));
      timeoutIdRef.current = setTimeout(tick, nextDelay);
    };

    expectedRef.current = Date.now() + delay;
    timeoutIdRef.current = setTimeout(tick, delay);

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
      expectedRef.current = null;
      runningRef.current = false;
    };
  }, [delay]);

  useEffect(() => {
    if (delay == null) return;

    const onVisible = async () => {
      if (document.hidden) return;
      if (!runningRef.current) {
        runningRef.current = true;
        try {
          await savedCallback.current();
        } finally {
          runningRef.current = false;
        }
      }
      expectedRef.current =
        Date.now() + (typeof delay === "number" ? delay : 0);
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [delay]);
}

const VerseSidebar: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [time, setTime] = useState(new Date());
  const [dollarRate, setDollarRate] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [debits, setDebits] = useState<CurrentDebitInterface[]>([]);
  const [debitStatus, setDebitStatus] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);

  // Versículos (40s)
  useEffect(() => {
    const iv = setInterval(
      () => setCurrentIndex((i) => (i + 1) % verses.length),
      40_000
    );
    return () => clearInterval(iv);
  }, []);

  // Busca do status de débito
  const fetchDebitStatus = async () => {
    try {
      const { data } = await api.get<CurrentDebitApi[]>(
        `/subscription/company/current-payment`
      );

      const parsed: CurrentDebitInterface[] = (data ?? []).map((d) => ({
        charged_amount: Number(d.charged_amount),
        company_id: d.company_id,
        config_id: d.config_id,
        created_at: d.created_at,
        due_date: d.due_date,
        is_in_debt: Number(d.is_in_debt),
        paid_amount: Number(d.paid_amount),
        payment_date: d.payment_date,
        subscription_id: d.subscription_id,
        updated_at: d.updated_at
      }));

      setDebits(parsed);

      const now = new Date();
      const hasOpenDebt = parsed.some((d) => {
        const overdue = new Date(d.due_date) < now;
        const notFullyPaid = d.paid_amount < d.charged_amount;
        return d.is_in_debt === 1 || (overdue && notFullyPaid);
      });

      setDebitStatus(hasOpenDebt);
    } catch (err) {
      console.error(err);
      setDebits([]);
      setDebitStatus(false);
    }
  };

  // Checagem imediata ao carregar a página, para não esperar o primeiro
  // intervalo do polling abaixo para saber se a empresa está inadimplente.
  useEffect(() => {
    fetchDebitStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quem realmente muda o status de inadimplência é o cron do backend
  // (a cada 10 min). Aqui só precisamos reler esse status periodicamente,
  // então 10s era desnecessariamente agressivo — 3h é suficiente, e o
  // useInterval já rechecha sozinho quando a aba volta a ficar visível.
  useInterval(() => {
    fetchDebitStatus();
  }, 3 * 60 * 60 * 1000);

  // Relógio
  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1_000);
    return () => clearInterval(iv);
  }, []);

  // PTAX
  useEffect(() => {
    const fetchPTAX = async () => {
      try {
        const res = await fetch(
          "https://api.bcb.gov.br/dados/serie/bcdata.sgs.1/dados/ultimos/1?formato=json"
        );
        if (!res.ok) throw new Error(`BCB SGS devolveu ${res.status}`);
        const json: Array<{ data: string; valor: string }> = await res.json();
        if (json.length === 0) throw new Error("Nenhum dado retornado");
        const ptax = parseFloat(json[0].valor.replace(",", "."));
        setDollarRate(ptax);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Não foi possível obter PTAX do SGS");
      }
    };

    fetchPTAX();
    const iv = setInterval(fetchPTAX, 60 * 60 * 1_000);
    return () => clearInterval(iv);
  }, []);

  const formattedTime = time.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const formattedRate =
    dollarRate !== null
      ? `R$ ${dollarRate.toFixed(3).replace(".", ",")}`
      : "...";

  const [reference, text] = verses[currentIndex].split(" - ", 2);

  return (
    <>
      <Box
        sx={{
          width: 172,
          height: "100%",
          flexShrink: 0,
          borderLeft: "1px solid var(--border)",
          background: "var(--surface-2)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 1.25,
          gap: 0.5,
          overflowY: "auto"
        }}
      >
        {/* Relógio */}
        <CardVerse style={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              color: "text.disabled",
              mb: 0.5
            }}
          >
            Agora
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: "1.5rem",
              letterSpacing: "-0.01em",
              fontVariantNumeric: "tabular-nums",
              background: "var(--gradient-brand)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}
          >
            {formattedTime}
          </Typography>
        </CardVerse>

        {/* Dólar Hoje */}
        <CardVerse style={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              color: "text.disabled",
              mb: 0.5
            }}
          >
            Dólar Hoje
          </Typography>
          <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.1rem" }}>
            {error ?? formattedRate}
          </Typography>
        </CardVerse>

        {/* Versículo */}
        <CardVerse style={{ textAlign: "justify" }}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              color: "text.disabled",
              textAlign: "center",
              display: "block"
            }}
          >
            Versículo
          </Typography>
          <Divider sx={{ my: 1, borderColor: "var(--border)" }} />
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.secondary", lineHeight: 1.5 }}
          >
            <strong style={{ color: "var(--accent-2)" }}>{reference}</strong>
            <br />
            {text}
          </Typography>
        </CardVerse>

        {/* Alerta + botão para abrir modal */}
        {debitStatus && (
          <CardVerse
            style={{ textAlign: "center", padding: "10px 8px", width: "88%" }}
          >
            <AiTwotoneAlert color="#fb7185" size={25} />
            <Typography variant="caption" display="block" sx={{ mb: 1, color: "text.secondary" }}>
              Mensalidade em aberto
            </Typography>
            <Button
              onClick={() => setOpenModal(true)}
              fullWidth
              variant="contained"
              color="error"
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Ver débitos / Pagar
            </Button>
          </CardVerse>
        )}
      </Box>

      {/* Modal de pagamentos */}
      <PaymentModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        debits={debits}
      />
    </>
  );
};

export default VerseSidebar;
